/**
 * Token Vault Explorer
 *
 * Demo Step 4: Open the token vault. Show the AES-256-GCM encrypted value
 * with KMS-wrapped DEK.
 *
 * Visualizes the envelope encryption pattern:
 *   1. Plaintext → AES-256-GCM encryption with random DEK
 *   2. DEK → wrapped (encrypted) by external KMS
 *   3. Ciphertext + Wrapped DEK stored together in vault
 *   4. On resolve: KMS unwraps DEK → DEK decrypts ciphertext
 */

import React, { useState, useEffect } from 'react';

const KEY_PROVIDERS = {
  LOCAL:     { label: 'Local Key Derivation', icon: '🔑', color: '#6b7280' },
  HASHICORP: { label: 'HashiCorp Vault Transit', icon: '🏛', color: '#000' },
  AWS_KMS:   { label: 'AWS KMS', icon: '☁', color: '#ff9900' },
  AZURE_KV:  { label: 'Azure Key Vault', icon: '🔷', color: '#0078d4' },
};

export function TokenVaultExplorer({ tokens = [], workflowRunId }) {
  const [selectedToken, setSelectedToken] = useState(null);
  const [showDecryptFlow, setShowDecryptFlow] = useState(false);
  const [decryptStep, setDecryptStep] = useState(0);

  // Demo-quality token entries when no live data
  const displayTokens = tokens.length > 0 ? tokens : [
    {
      tokenId: 'dvt_a1b2c3d4-e5f6-7890-abcd-ef1234567890',
      fieldName: 'ssn',
      dataType: 'SSN',
      originalValue: '***-**-6789',
      ciphertext: 'AQIDAHhK9Y2bN7xT...kF3mG8hJ2nP5qR7sT9w==',
      iv: 'x4K9Y2bN7xT+mRw=',
      wrappedDek: 'AQEDAHgF7B2...encrypted-by-kms...pK8mN3qR==',
      keyProvider: 'AWS_KMS',
      keyIdentifier: 'arn:aws:kms:us-east-1:123456789:key/abcd-1234',
      encryption: 'AES-256-GCM',
      createdAt: new Date().toISOString(),
      ttlHours: 24,
      scope: 'workflow-execution',
      tenantIsolated: true,
    },
    {
      tokenId: 'dvt_f7g8h9i0-j1k2-3456-lmno-pq7890123456',
      fieldName: 'credit_card',
      dataType: 'CREDIT_CARD',
      originalValue: '****-****-****-4242',
      ciphertext: 'BRJFBKhL0Z3cP...nG5jK7mN9pQ2rT4v==',
      iv: 'hL0Z3cP6fH8jK2=',
      wrappedDek: 'AQEDAHgR9K...encrypted-by-vault-transit...mX7nY==',
      keyProvider: 'HASHICORP',
      keyIdentifier: 'transit/keys/agentlets-tenant-001',
      encryption: 'AES-256-GCM',
      createdAt: new Date().toISOString(),
      ttlHours: 24,
      scope: 'workflow-execution',
      tenantIsolated: true,
    },
  ];

  const handleShowDecrypt = (token) => {
    setSelectedToken(token);
    setShowDecryptFlow(true);
    setDecryptStep(0);
    // Auto-advance through decryption visualization steps
    const steps = [1, 2, 3, 4];
    steps.forEach((step, i) => {
      setTimeout(() => setDecryptStep(step), (i + 1) * 1200);
    });
  };

  const getProviderInfo = (provider) => KEY_PROVIDERS[provider] || KEY_PROVIDERS.LOCAL;

  return (
    <div style={{
      background: '#fff',
      borderRadius: 8,
      border: '1px solid #e5e7eb',
      overflow: 'hidden',
      fontSize: 13,
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3a5f 0%, #1e40af 100%)',
        color: '#fff',
        padding: '10px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}>
        <span style={{ fontSize: 16 }}>🔐</span>
        <span style={{ fontWeight: 700, fontSize: 13 }}>Tokenized Data Vault</span>
        <span style={{ marginLeft: 'auto', fontSize: 11, opacity: 0.7 }}>
          {displayTokens.length} active tokens
        </span>
      </div>

      {/* Encryption Info Banner */}
      <div style={{
        background: '#eff6ff',
        borderBottom: '1px solid #bfdbfe',
        padding: '6px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 11,
        color: '#1e40af',
      }}>
        <span>🔒</span>
        <span>Envelope Encryption: AES-256-GCM with KMS-wrapped Data Encryption Keys (DEK)</span>
      </div>

      {/* Token List */}
      <div style={{ maxHeight: 500, overflowY: 'auto' }}>
        {displayTokens.map((token, i) => {
          const provider = getProviderInfo(token.keyProvider);
          const isSelected = selectedToken?.tokenId === token.tokenId;

          return (
            <div key={token.tokenId || i} style={{
              borderBottom: '1px solid #f3f4f6',
              background: isSelected ? '#f0f9ff' : (i % 2 === 1 ? '#fafafa' : '#fff'),
              cursor: 'pointer',
              transition: 'background 0.15s',
            }}>
              {/* Token Summary Row */}
              <div
                style={{ padding: '10px 14px' }}
                onClick={() => setSelectedToken(isSelected ? null : token)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <code style={{
                    background: '#f3f4f6',
                    padding: '2px 6px',
                    borderRadius: 3,
                    fontSize: 11,
                    color: '#374151',
                    fontFamily: 'monospace',
                  }}>
                    {token.tokenId.substring(0, 24)}...
                  </code>
                  <span style={{
                    background: '#fef2f2',
                    color: '#991b1b',
                    padding: '1px 6px',
                    borderRadius: 3,
                    fontSize: 10,
                    fontWeight: 600,
                  }}>
                    {token.dataType}
                  </span>
                  <span style={{
                    marginLeft: 'auto',
                    background: provider.color + '20',
                    color: provider.color,
                    padding: '1px 6px',
                    borderRadius: 3,
                    fontSize: 10,
                    fontWeight: 600,
                  }}>
                    {provider.icon} {provider.label}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>
                  Field: <strong>{token.fieldName}</strong> | Masked: <code>{token.originalValue}</code> | TTL: {token.ttlHours}h
                </div>
              </div>

              {/* Expanded Detail View */}
              {isSelected && (
                <div style={{ padding: '0 14px 12px 14px' }}>
                  {/* Envelope Encryption Visualization */}
                  <div style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    padding: 12,
                    marginBottom: 8,
                  }}>
                    <div style={{ fontWeight: 600, color: '#334155', fontSize: 11, marginBottom: 8 }}>
                      ENVELOPE ENCRYPTION DETAILS
                    </div>

                    {/* Ciphertext */}
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2 }}>Ciphertext (AES-256-GCM)</div>
                      <div style={{
                        fontFamily: 'monospace',
                        fontSize: 10,
                        color: '#1c1917',
                        background: '#292524',
                        padding: '6px 8px',
                        borderRadius: 4,
                        wordBreak: 'break-all',
                        color: '#94a3b8',
                      }}>
                        {token.ciphertext}
                      </div>
                    </div>

                    {/* IV */}
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2 }}>Initialization Vector (IV)</div>
                      <div style={{
                        fontFamily: 'monospace',
                        fontSize: 10,
                        background: '#292524',
                        padding: '6px 8px',
                        borderRadius: 4,
                        color: '#94a3b8',
                      }}>
                        {token.iv}
                      </div>
                    </div>

                    {/* Wrapped DEK */}
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2 }}>
                        KMS-Wrapped Data Encryption Key (DEK)
                        <span style={{
                          marginLeft: 6,
                          background: '#dcfce7',
                          color: '#166534',
                          padding: '1px 4px',
                          borderRadius: 2,
                          fontSize: 9,
                        }}>
                          NEVER STORED IN PLAINTEXT
                        </span>
                      </div>
                      <div style={{
                        fontFamily: 'monospace',
                        fontSize: 10,
                        background: '#292524',
                        padding: '6px 8px',
                        borderRadius: 4,
                        color: '#f59e0b',
                        wordBreak: 'break-all',
                      }}>
                        {token.wrappedDek}
                      </div>
                    </div>

                    {/* Key Identifier */}
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2 }}>Key Identifier ({provider.label})</div>
                      <div style={{
                        fontFamily: 'monospace',
                        fontSize: 10,
                        background: '#292524',
                        padding: '6px 8px',
                        borderRadius: 4,
                        color: '#67e8f9',
                      }}>
                        {token.keyIdentifier}
                      </div>
                    </div>

                    {/* Security Properties */}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                      {[
                        { label: 'Tenant-Isolated', value: token.tenantIsolated },
                        { label: 'Workflow-Scoped', value: token.scope === 'workflow-execution' },
                        { label: 'Customer-Managed Key', value: token.keyProvider !== 'LOCAL' },
                        { label: 'HSM-Backed', value: ['AWS_KMS', 'AZURE_KV'].includes(token.keyProvider) },
                      ].map((prop, j) => (
                        <span key={j} style={{
                          fontSize: 10,
                          padding: '2px 6px',
                          borderRadius: 3,
                          background: prop.value ? '#dcfce7' : '#f3f4f6',
                          color: prop.value ? '#166534' : '#9ca3af',
                          fontWeight: 500,
                        }}>
                          {prop.value ? '✓' : '✕'} {prop.label}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Decrypt Flow Visualization Button */}
                  <button
                    onClick={() => handleShowDecrypt(token)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      background: '#1e40af',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 4,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    ▶ Show Envelope Decryption Flow
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Envelope Decryption Flow Visualization Modal */}
      {showDecryptFlow && selectedToken && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}
          onClick={() => setShowDecryptFlow(false)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 24,
              maxWidth: 600,
              width: '90%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px', color: '#1e3a5f', fontSize: 16 }}>
              Envelope Encryption / Decryption Flow
            </h3>

            {/* Flow Steps */}
            {[
              { step: 1, label: 'Application requests DEK from KMS', detail: `Call ${getProviderInfo(selectedToken.keyProvider).label} → GenerateDataKey`, icon: '📤', color: '#3b82f6' },
              { step: 2, label: 'KMS returns plaintext DEK + wrapped DEK', detail: 'Plaintext DEK held in memory only (never persisted)', icon: '📥', color: '#0a766b' },
              { step: 3, label: 'Encrypt data with plaintext DEK (AES-256-GCM)', detail: `Ciphertext: ${selectedToken.ciphertext.substring(0, 30)}...`, icon: '🔐', color: '#059669' },
              { step: 4, label: 'Store ciphertext + wrapped DEK (plaintext DEK destroyed)', detail: `Wrapped DEK: ${selectedToken.wrappedDek.substring(0, 30)}...`, icon: '💾', color: '#dc2626' },
            ].map(({ step, label, detail, icon, color }) => (
              <div key={step} style={{
                display: 'flex',
                gap: 12,
                padding: '10px 0',
                borderBottom: step < 4 ? '1px dashed #e5e7eb' : 'none',
                opacity: decryptStep >= step ? 1 : 0.3,
                transition: 'opacity 0.5s ease',
              }}>
                <div style={{
                  width: 32, height: 32,
                  borderRadius: '50%',
                  background: decryptStep >= step ? color : '#e5e7eb',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14,
                  flexShrink: 0,
                  transition: 'background 0.5s ease',
                }}>
                  {decryptStep >= step ? icon : step}
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: '#1f2937', fontSize: 13 }}>{label}</div>
                  <div style={{ fontSize: 11, color: '#6b7280', fontFamily: 'monospace', marginTop: 2 }}>{detail}</div>
                </div>
              </div>
            ))}

            <div style={{
              marginTop: 16,
              padding: '10px 14px',
              background: '#f0fdf4',
              border: '1px solid #86efac',
              borderRadius: 6,
              fontSize: 12,
              color: '#166534',
              fontWeight: 500,
              opacity: decryptStep >= 4 ? 1 : 0,
              transition: 'opacity 0.5s ease',
            }}>
              ✓ Sensitive plaintext NEVER leaves the application process boundary.
              Only the KMS can unwrap the DEK — even the database admin cannot decrypt the data.
            </div>

            <button
              onClick={() => setShowDecryptFlow(false)}
              style={{
                marginTop: 12,
                padding: '8px 16px',
                background: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TokenVaultExplorer;
